import {
    SovosBox,
    SovosCircularProgress,
    SovosPopover,
    SovosTextField,
    SovosTreeList,
    SovosTypography,
} from "mosaic-react";
import { TableRows } from "mosaic-react-icons";
import { ArrowDropDown } from "mosaic-react-icons";
import React, { useMemo, useState } from "react";
import AiConfidencePill from "../AiConfidencePill";
import { ENTITY_PICKER_FLAT_LIST_SX, FIELD_SPACING_SX } from "./constants";
import { getEntitySelectionKey } from "./entitySelectionKey";

const EntityAutocompleteField = ({
    aiEntityScore,
    disabled,
    entities,
    i18n,
    loading,
    onChange,
    suggestionScores = {},
    value,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const open = Boolean(anchorEl);
    const selectedEntity =
        entities.find((entity) => getEntitySelectionKey(entity) === value) ||
        entities.find((entity) => entity.file_type === value) ||
        null;

    const filteredEntities = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return entities;
        return entities.filter((entity) => entity.entity_name.toLowerCase().includes(query));
    }, [entities, searchQuery]);

    const treeData = useMemo(
        () =>
            filteredEntities.map((entity) => {
                const selectionKey = getEntitySelectionKey(entity);
                return {
                    id: selectionKey,
                    content: (
                        <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1, width: "100%" }}>
                            <TableRows sx={{ color: "action.active", fontSize: 20 }} />
                            <SovosTypography sx={{ flex: 1, minWidth: 0 }} variant="body2">
                                {entity.entity_name}
                            </SovosTypography>
                            {suggestionScores[selectionKey] != null && (
                                <AiConfidencePill score={suggestionScores[selectionKey]} />
                            )}
                        </SovosBox>
                    ),
                };
            }),
        [filteredEntities, suggestionScores],
    );

    const closePicker = () => {
        setAnchorEl(null);
        setSearchQuery("");
    };

    const handleSelect = (item) => {
        if (!item?.id) return;
        onChange(item.id);
        closePicker();
    };

    return (
        <>
            <SovosTextField
                disabled={disabled}
                fullWidth
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <SovosBox sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
                            {aiEntityScore !== null && <AiConfidencePill score={aiEntityScore} />}
                            <ArrowDropDown sx={{ color: "action.active" }} />
                        </SovosBox>
                    ),
                    sx: { cursor: disabled ? "default" : "pointer" },
                }}
                label={i18n`Entity`}
                onClick={(event) => {
                    if (!disabled) setAnchorEl(event.currentTarget);
                }}
                placeholder={i18n`Select entity`}
                sx={FIELD_SPACING_SX}
                value={selectedEntity?.entity_name || ""}
            />
            <SovosPopover
                anchorEl={anchorEl}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                data-testid="entity-picker-popover"
                onClose={closePicker}
                open={open}
                sx={{ zIndex: 1400 }}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
            >
                <SovosBox
                    sx={{
                        maxHeight: 320,
                        minWidth: anchorEl?.clientWidth || 552,
                        width: anchorEl?.clientWidth || 552,
                    }}
                >
                    <SovosBox
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            px: 1,
                            py: 0.5,
                        }}
                    >
                        <SovosTextField
                            fullWidth
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder={i18n`Search`}
                            size="small"
                            sx={{ maxWidth: "100%", width: "100%" }}
                            value={searchQuery}
                            variant="outlined"
                        />
                    </SovosBox>
                    <SovosBox sx={ENTITY_PICKER_FLAT_LIST_SX}>
                        {treeData.length > 0 ? (
                            <SovosTreeList
                                data={treeData}
                                initialSelectedItem={value || undefined}
                                onItemClick={handleSelect}
                            />
                        ) : (
                            <SovosTypography sx={{ p: 2, textAlign: "center" }} variant="body2">
                                {i18n`No results found`}
                            </SovosTypography>
                        )}
                    </SovosBox>
                </SovosBox>
            </SovosPopover>
            {loading && (
                <SovosCircularProgress
                    aria-label={i18n`Loading entities`}
                    size="1.25rem"
                    sx={FIELD_SPACING_SX}
                />
            )}
        </>
    );
};

export default EntityAutocompleteField;
